import { API_BASE_URL } from '../config/api';
import { showSessionExpiredModal } from '../utils/sessionExpiredModal';
import { clearAllCache } from './cacheService';
import i18n from '../i18n';

export class RateLimitError extends Error {
  rateLimit: number;
  retryAfter: number;
  upgradeRequired: boolean;

  constructor(message: string, rateLimit: number, retryAfter: number, upgradeRequired: boolean) {
    super(message);
    this.name = 'RateLimitError';
    this.rateLimit = rateLimit;
    this.retryAfter = retryAfter;
    this.upgradeRequired = upgradeRequired;
  }
}

// Log the API URL being used (for debugging)
console.log('USING API URL:', API_BASE_URL);

// Helper to get auth token from localStorage
const getAuthToken = () => localStorage.getItem('token');

// Helper to get current language for Accept-Language header
const getAcceptLanguage = () => i18n.language?.substring(0, 2) || 'en';

// Handle 401 Unauthorized globally — show session expired modal with countdown
export const handleUnauthorized = () => {
  clearAllCache();
  showSessionExpiredModal();
};

// Generic fetch wrapper with auth
async function fetchWithAuth<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getAcceptLanguage(),
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    // Check if it's an authentication error
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Session expired. Please login again.');
    }
    
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API error: ${response.status}`);
  }
  
  return response.json();
}

export interface ReportsFilters {
  fromDate?: Date;
  toDate?: Date;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export const api = {
  // Get the base URL for direct access when needed (file downloads, etc.)
  getBaseUrl: () => API_BASE_URL,
  
  // General HTTP methods
  get: async <T>(endpoint: string, options?: Record<string, any>): Promise<{ data: T }> => {
    const token = getAuthToken();
    let url = `${API_BASE_URL}${endpoint}`;
    
    // Extract responseType from options if present
    const responseType = options?.responseType;
    const params = options?.responseType ? undefined : options;
    
    // Add query params if provided (and not responseType option)
    if (params && !options?.responseType) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'responseType') {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': getAcceptLanguage(),
        // Don't send Accept: application/json for blob requests so backend streams the file
        ...(responseType !== 'blob' ? { 'Accept': 'application/json' } : {}),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
    
    if (!response.ok) {
      if (response.status === 401) { handleUnauthorized(); throw new Error('Session expired. Please login again.'); }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }
    
    // Handle blob response type for file downloads
    if (responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob as unknown as T };
    }
    
    return response.json();
  },
  
  post: async <T>(endpoint: string, data: any, options?: { responseType?: string }): Promise<{ data: T }> => {
    const token = getAuthToken();
    const isFormData = data instanceof FormData;
    const responseType = options?.responseType;

    const headers: Record<string, string> = {
      'Accept-Language': getAcceptLanguage(),
      ...(responseType !== 'blob' ? { 'Accept': 'application/json' } : {}),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    // Only set Content-Type for non-FormData requests
    // Browser will set the correct Content-Type with boundary for FormData
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });

    if (!response.ok) {
      if (response.status === 401) { handleUnauthorized(); throw new Error('Session expired. Please login again.'); }
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 429 && errorData.rate_limit) {
        throw new RateLimitError(
          errorData.message || 'Rate limit exceeded',
          errorData.rate_limit,
          errorData.retry_after ?? 3600,
          errorData.upgrade_required ?? false,
        );
      }
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    if (responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob as unknown as T };
    }
    
    return response.json();
  },
  
  put: async <T>(endpoint: string, data: any): Promise<{ data: T }> => {
    const token = getAuthToken();
    const isFormData = data instanceof FormData;

    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Accept-Language': getAcceptLanguage(),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
    
    // Only set Content-Type for non-FormData requests
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    
    if (!response.ok) {
      if (response.status === 401) { handleUnauthorized(); throw new Error('Session expired. Please login again.'); }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }
    
    return response.json();
  },
  
  delete: async <T>(endpoint: string): Promise<{ data: T }> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': getAcceptLanguage(),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
    
    if (!response.ok) {
      if (response.status === 401) { handleUnauthorized(); throw new Error('Session expired. Please login again.'); }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }
    
    return response.json();
  },
};
